// Route + layout switcher. The UI-kit/shell owner replaces the placeholder layouts in
// components/app/* with the real shell; this file only wires routes to layouts and guards.
import { Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router'
import { routes, type Layout } from './routes'
import { useSession } from '@/lib/session'
import { StoreShell, PlatformShell, BareShell, PublicShell, FullShell, PageFallback } from '@/components/app/Shells'
import { RouteErrorBoundary } from '@/components/app/RouteErrorBoundary'

function Guard({ layout, children }: { layout: Layout; children: React.ReactNode }) {
  const { me, loading, store } = useSession()
  const loc = useLocation()
  if (layout === 'raw') return <>{children}</>
  if (loading) return <PageFallback />
  if (layout === 'public') return me ? <Navigate to="/" replace /> : <>{children}</>
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />
  if (layout === 'platform' && !me.user.isPlatformAdmin) return <Navigate to="/" replace />
  if ((layout === 'store' || layout === 'full') && !me.stores.length) return <Navigate to="/onboarding" replace />
  if ((layout === 'store' || layout === 'full') && !store) return <PageFallback />
  return <>{children}</>
}

const SHELL: Record<Layout, React.ComponentType<{ children: React.ReactNode }>> = {
  public: PublicShell, bare: BareShell, store: StoreShell, full: FullShell, platform: PlatformShell, raw: BareShell,
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {routes.map(r => {
          const Shell = SHELL[r.layout]
          return (
            <Route key={r.path} path={r.path} element={
              <Guard layout={r.layout}>
                <Shell>
                  <RouteErrorBoundary>
                    <Suspense fallback={<PageFallback />}><r.Component /></Suspense>
                  </RouteErrorBoundary>
                </Shell>
              </Guard>
            } />
          )
        })}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
